import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Container,
  Card,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { ShimmerTable } from "react-shimmer-effects";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { isMobile } from "react-device-detect";
import moment from "moment";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { FaBan, FaSearch } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";

const BlockedLeadsList = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [lead, setLead] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  const normalizeRow = (item, index) => {
    const firstname = item.firstname || "";
    const lastname = item.lastname || "";
    const leadname = [firstname, lastname].filter(Boolean).join(" ") || "—";
    const full_number = `${item.country_code || ""}${item.whatsapp_number || ""}`;
    return {
      ...item,
      serial: index + 1,
      leadname,
      full_number,
    };
  };

  const loadBlockedLeads = async () => {
    setIsSpinner(false);
    try {
      const res = await WhatsAppAPI.fetchBlockedLeads();
      if (res && res.success) {
        const records = res.records || [];
        const withSerial = records.map(normalizeRow);
        setBody(withSerial);
        setLead(withSerial);
      } else {
        setBody([]);
        setLead([]);
        toast.error(res?.message || "Failed to load blocked leads.");
      }
    } catch (err) {
      console.error(err);
      setBody([]);
      setLead([]);
      toast.error("Error loading blocked leads.");
    } finally {
      setIsSpinner(true);
    }
  };

  useEffect(() => {
    loadBlockedLeads();
  }, []);

  useEffect(() => {
    if (!lead || lead.length === 0) return;
    let filtered = [...lead];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (row) =>
          row.leadname?.toLowerCase().includes(q) ||
          row.email?.toLowerCase().includes(q) ||
          row.whatsapp_number?.toString().includes(searchQuery) ||
          row.full_number?.toString().includes(searchQuery) ||
          row.firstname?.toLowerCase().includes(q) ||
          row.lastname?.toLowerCase().includes(q),
      );
    }
    setBody(filtered);
  }, [searchQuery, lead]);

  const header = useMemo(() => {
    const cols = [];
    if (!isMobile) {
      cols.push(
        { title: "S.No", prop: "serial" },
        {
          title: "Name",
          prop: "leadname",
          isFilterable: true,
        },
        {
          title: "WhatsApp",
          prop: "whatsapp_number",
          isFilterable: true,
          cell: (row) => (
            <span>
              {row.country_code}{" "}
              {row.display_whatsapp_number ||
                formatPhoneNumber(row.whatsapp_number)}
            </span>
          ),
        },
        {
          title: "Created At",
          prop: "createddate",
          cell: (row) => formatDate(row.createddate),
        },
      );
    } else {
      cols.push({
        title: "Info",
        prop: "leadname",
        cell: (row) => (
          <div className="mobilecard">
            <Link
              to={"/leads/" + row.id}
              state={row}
              style={{ fontSize: "1.2rem" }}
            >
              {row.leadname || "—"}
            </Link>
            <span>
              {row.display_whatsapp_number ||
                formatPhoneNumber(row.whatsapp_number)}
            </span>
            <div className="mt-2 text-muted small">
              {formatDate(row.createddate)}
            </div>
          </div>
        ),
      });
    }
    return cols;
  }, []);

  const labels = { beforeSelect: " " };

  return (
    <>
      <Container className="mt-5 mb-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">
              <FaBan className="me-2 text-danger" /> OTP Out / Blocked Leads
            </h5>
            <small>
              Leads who have blocked us or are OTP out (read-only list).
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <div className="bg-white shadow-sm border-0 rounded-3 p-4">
              <h5 className="fw-semibold mb-1">Blocked leads</h5>

              {isSpinner ? (
                <DatatableWrapper
                  body={body}
                  keyField="id"
                  headers={header}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 10,
                      options: [10, 20, 30, 50],
                    },
                  }}
                >
                  <Row className="mb-3">
                    <Col
                      lg={4}
                      sm={10}
                      xs={10}
                      className="d-flex align-items-end"
                    >
                      <Filter className="p-2" />
                    </Col>
                    <Col lg={4} sm={2} xs={2}>
                      <PaginationOptions labels={labels} />
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table
                      striped
                      bordered
                      className="related-list-table"
                      responsive="sm"
                    >
                      <TableHeader />
                      <TableBody />
                    </Table>
                  </div>
                  <Pagination />
                </DatatableWrapper>
              ) : (
                <ShimmerTable row={10} col={4} />
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <ToastContainer />
    </>
  );
};

export default BlockedLeadsList;
